export const HOME = {
  seo: {
    title: "Zenspark Global | Technology Decisions and Accountable Delivery",
    description:
      "Zenspark helps growth-company technology leaders build accountable engineering capability and take priority technology and AI initiatives into production.",
  },
  hero: {
    eyebrow: "Technology Engineering & Delivery · Bengaluru",
    copy: "Zenspark helps technology leaders at revenue-generating growth companies turn priority decisions into accountable engineering capability and production solutions.",
    primary: "Bring us a technology decision",
    secondary: "See how Zenspark works",
    trust: "Founder-led · Bengaluru delivery base · Since 2025",
  },
  tension: {
    h2: "Growth companies rarely lack technology options. They lack one accountable path from decision to result.",
    body: [
      "The initiative may be important, but the business case is still unclear. A capable internal team may be stretched. Vendors may own individual tasks while nobody owns the outcome. AI pilots may demonstrate possibility without changing the workflow.",
      "Zenspark closes the gap between the decision and the delivery required to make it real.",
    ],
  },
  fit: {
    h2: "Built for companies that have outgrown fragmented delivery.",
    body: "Zenspark is designed for operating companies with revenue, ambition, and a meaningful technology dependency. The strongest fit is a leadership team facing one or more of these situations:",
    items: [
      "A priority initiative has no clear owner from business decision through production.",
      "Internal engineering leadership needs dependable capability without another staffing vendor.",
      "Multiple vendors create coordination work, uneven quality, or unclear accountability.",
      "An AI opportunity is promising but has not moved into a governed operating workflow.",
      "A product, platform, or modernisation decision is slowing growth.",
    ],
  },
  routes: {
    h2: "Two routes. One accountable outcome.",
    items: [
      {
        label: "Build",
        title: "Build accountable engineering capability.",
        body: "Establish or scale an integrated delivery capability through Extended PODs and other fit-for-purpose models. Zenspark makes decision rights, governance, cadence, quality, and ownership visible from the beginning.",
        cta: "Design the capability",
      },
      {
        label: "Implement",
        title: "Take a priority initiative into production.",
        body: "Turn a defined business problem into a working technology solution across AI and intelligent automation, data and cloud platforms, enterprise modernisation, or digital products.",
        cta: "Discuss the initiative",
      },
    ],
    transform: "Transform is the result: stronger capability, better decisions, more efficient operations, improved customer value, or faster growth.",
  },
  diff: {
    h2: "Zenspark owns the space between the decision and the result.",
    pillars: [
      { title: "Decision clarity", text: "Define the problem, business value, economics, risks, and success measures before prescribing technology or team structure." },
      { title: "Delivery ownership", text: "Connect architecture, capability, implementation, governance, and adoption under one accountable path." },
      { title: "Visible control", text: "Make priorities, decisions, progress, quality, dependencies, and risks understandable to both business and technology leaders." },
    ],
  },
  ai: {
    eyebrow: "AI Adoption & Implementation",
    h2: "AI value begins where the pilot ends.",
    body: [
      "Zenspark identifies a worthwhile use case, prepares the required data and governance, builds the solution, integrates it into the operating workflow, and defines how performance will be measured and improved.",
      "AI may be the right answer, part of the answer, or the wrong answer. The business problem and economics decide.",
    ],
    memory: "Adopt AI. Build with it. Move faster.",
    cta: "Discuss the use case",
  },
  method: {
    h2: "One operating rhythm from decision through delivery.",
    steps: [
      { title: "Diagnose", text: "Define the business problem, value hypothesis, economics, risks, and success measures." },
      { title: "Design", text: "Choose the architecture, delivery model, team, plan, governance, and trade-offs." },
      { title: "Deliver", text: "Build and implement with visible cadence, quality controls, and decision ownership." },
      { title: "Operate or Transfer", text: "Run, improve, support, or transfer the capability according to the agreed model." },
    ],
  },
  evidence: {
    h2: "Proof should show how delivery is controlled.",
    body: "Zenspark does not use invented success metrics. Until client-approved case evidence is available, we make the delivery mechanism visible through representative artefacts:",
    items: [
      "Decision brief and value hypothesis",
      "Solution or capability blueprint",
      "Governance and decision-rights model",
      "Delivery board and milestone view",
      "Risk, dependency, and decision log",
      "Outcome scorecard and transfer plan",
    ],
    closing: "These artefacts demonstrate the operating discipline. They are not presented as client results.",
    cta: "See how delivery is controlled",
  },
  founder: {
    h2: "Founder-led accountability, grounded in enterprise delivery.",
    body: "Arunava Ghosh founded Zenspark after nearly two decades in enterprise technology and delivery leadership across Deloitte, PwC, IBM, and Wells Fargo. His experience before Zenspark includes responsibility for a $75M+ portfolio, leadership of 1,500+ professionals across seven regions, flagship-account growth beyond $8M, and 95%+ customer satisfaction in prior roles.",
    disclaimer: "These are founder credentials from experience before Zenspark, not Zenspark company results.",
    cta: "Read Arunava’s perspective",
  },
  jiya: {
    eyebrow: "In development",
    h2: "Jiya will make the first technology conversation more useful.",
    body: "Jiya by Zenspark is being designed to clarify technology decisions, identify missing information, compare practical options, explain assumptions, and create an actionable path before a prospect is asked to attend a sales call.",
  },
  closing: {
    h2: "What technology decision is slowing progress?",
    body: "Share the business objective, the current constraint, and what must be decided next. Zenspark will respond with the most relevant starting path.",
    cta: "Bring us the decision",
  },
};

export const ABOUT = {
  seo: {
    title: "Why Zenspark | Decision-to-Delivery Technology Partner",
    description:
      "Zenspark Global helps revenue-generating growth companies connect better technology decisions with accountable capability and production delivery.",
  },
  hero: {
    eyebrow: "Why Zenspark",
    h1: "The market did not need another technology vendor. It needed clearer ownership.",
    copy: "Zenspark was founded to close the gap between deciding what matters and delivering it with enough discipline to change the business.",
  },
  why: {
    h2: "Technology work becomes expensive when ownership is fragmented.",
    body: [
      "Large providers can offer breadth but distance senior attention from the work. Smaller vendors can offer flexibility but leave governance and integration to the client. Staffing firms can add people without owning delivery.",
      "Zenspark takes a different position: begin with the decision, select the smallest practical delivery model, and remain accountable through production and adoption.",
    ],
  },
  beliefs: [
    { title: "Technology is not the objective. Business progress is.", text: "Every engagement should make the intended business change, success measures, risks, decision rights, and delivery ownership visible." },
    { title: "Clarity comes before capability.", text: "A team or platform is useful only when it is aligned to the right problem and operating outcome." },
    { title: "Accountability must survive hand-offs.", text: "Architecture, engineering, governance, adoption, and operations should not become separate excuses." },
    { title: "AI earns its place through value.", text: "Zenspark uses AI where it improves the solution or the way it is delivered, not because it is fashionable." },
  ],
  founder: {
    h2: "Built from lessons learned across nearly two decades of enterprise delivery.",
    body: [
      "Before founding Zenspark, Arunava Ghosh worked across Deloitte, PwC, IBM, and Wells Fargo. He saw the same pattern at different scales: technology rarely fails because people lack ambition. It fails when the decision is unclear, accountability fragments, or implementation stops before the business changes.",
      "His prior-career experience includes responsibility for a $75M+ portfolio, leadership of more than 1,500 professionals across seven regions, flagship-account growth beyond $8M, and 95%+ customer satisfaction in prior roles.",
      "Zenspark applies that experience with the direct attention and operating discipline required by growth companies.",
    ],
    disclaimer: "Founder credentials from experience before Zenspark, not Zenspark company results.",
  },
  identity: [
    ["Display", "Zenspark Global Corporation"],
    ["Legal", "Zenspark Global Corporation Pvt Ltd"],
    ["Established", "Since 2025"],
    ["Delivery base", "Bengaluru, India"],
  ],
  closing: {
    h2: "Start with the decision, not the vendor presentation.",
    cta: "Bring us a technology decision",
  },
};
